/**
 * Comment Section Component
 * List of comments with form to add new ones
 */

import React from 'react';
import { MessageSquare } from 'lucide-react';
import { useComments } from '@/hooks';
import { CommentForm } from './CommentForm';
import { CommentItem } from './CommentItem';
import { LoadingState } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';

interface CommentSectionProps {
  ticketId: string;
}

export function CommentSection({ ticketId }: CommentSectionProps) {
  const { data: comments, isLoading, error, refetch } = useComments(ticketId);

  if (isLoading) {
    return <LoadingState message="Loading comments..." />;
  }

  if (error) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Failed to load comments'}
        retry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <MessageSquare className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Comments {comments && `(${comments.length})`}
        </h3>
      </div>

      {/* Add comment form */}
      <CommentForm ticketId={ticketId} />

      {/* Comments list */}
      <div className="space-y-4">
        {comments && comments.length > 0 ? (
          comments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} />
          ))
        ) : (
          <EmptyState
            title="No comments yet"
            description="Be the first to comment on this ticket"
            icon={<MessageSquare className="h-16 w-16" />}
          />
        )}
      </div>
    </div>
  );
}
